/* eslint-disable react/no-unescaped-entities */
import { useState, useCallback, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, CheckCircle2, Users } from 'lucide-react';

/**
 * Phase 326h — Merge Duplicate Students dialog.
 * ----------------------------------------------
 * Loads groups of rows that share the same (email, name) within the
 * academy. For each group the owner picks ONE keeper; the rest get
 * merged into it (dependent rows reassigned, duplicates deleted).
 */
export default function MergeDuplicatesDialog({ open, onOpenChange, onMerged }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selectedKeepers, setSelectedKeepers] = useState({});
  const [merging, setMerging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/students/duplicates');
      setGroups(res.data.groups || []);
      const initial = {};
      (res.data.groups || []).forEach((g, idx) => {
        initial[idx] = g.suggested_keeper_id;
      });
      setSelectedKeepers(initial);
    } catch (err) {
      toast.error('Could not load duplicate groups.');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleMergeAll = useCallback(async () => {
    setMerging(true);
    let merged = 0;
    let failed = 0;
    for (let idx = 0; idx < groups.length; idx += 1) {
      const g = groups[idx];
      const keeper = selectedKeepers[idx];
      if (!keeper) continue;
      const duplicateIds = g.rows.map((r) => r.id).filter((id) => id !== keeper);
      if (!duplicateIds.length) continue;
      try {
        await api.post('/students/merge-duplicates', {
          keeper_id: keeper,
          duplicate_ids: duplicateIds,
        });
        merged += duplicateIds.length;
      } catch (err) {
        failed += 1;
      }
    }
    setMerging(false);
    if (merged > 0) {
      toast.success(`Merged ${merged} duplicate row${merged === 1 ? '' : 's'} successfully.`);
      onMerged?.();
      onOpenChange?.(false);
    } else if (failed > 0) {
      toast.error('Merge failed. Try refreshing and retrying.');
    } else {
      toast.info('Nothing to merge.');
    }
  }, [groups, selectedKeepers, onMerged, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="merge-duplicates-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-amber-600" />
            Merge Duplicate Students
          </DialogTitle>
          <DialogDescription>
            Found rows sharing the same email + name? Pick which row to keep —
            the rest get merged in (fees, attendance and scorecards follow the
            keeper).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Scanning your roster…
          </div>
        ) : groups.length === 0 ? (
          <div
            className="text-center py-10 flex flex-col items-center gap-2"
            data-testid="merge-duplicates-empty"
          >
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="text-slate-700 font-medium">No duplicates found.</p>
            <p className="text-slate-500 text-xs">
              Your roster is squeaky clean — every email + name is unique.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                <strong>{groups.length}</strong> group{groups.length === 1 ? '' : 's'}{' '}
                detected. The oldest row is suggested as the keeper by default —
                feel free to override.
              </p>
            </div>
            {groups.map((g, idx) => (
              <div
                key={`${g.key.email}-${g.key.name}`}
                className="border rounded-lg overflow-hidden bg-white"
                data-testid={`merge-duplicates-group-${idx}`}
              >
                <div className="px-3 py-2 bg-slate-50 border-b flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-semibold text-slate-900">{g.key.name}</span>
                    <span className="text-slate-500"> · {g.key.email}</span>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                    {g.rows.length} rows
                  </Badge>
                </div>
                <div className="divide-y">
                  {g.rows.map((r) => {
                    const isKeeper = selectedKeepers[idx] === r.id;
                    return (
                      <label
                        key={r.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-emerald-50 ${
                          isKeeper ? 'bg-emerald-50/70' : 'bg-white'
                        }`}
                        data-testid={`merge-duplicates-row-${r.id}`}
                      >
                        <input
                          type="radio"
                          name={`keeper-${idx}`}
                          checked={isKeeper}
                          onChange={() => setSelectedKeepers((s) => ({ ...s, [idx]: r.id }))}
                          className="h-4 w-4 text-emerald-600"
                        />
                        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-4 gap-1 sm:gap-3 text-xs">
                          <div>
                            <div className="text-slate-500">ID</div>
                            <div className="font-mono font-medium text-slate-900">
                              {r.custom_id || '—'}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-500">DOB</div>
                            <div>{r.dob ? r.dob.slice(0, 10) : '—'}</div>
                          </div>
                          <div>
                            <div className="text-slate-500">Batch</div>
                            <div>{r.batch || '—'}</div>
                          </div>
                          <div>
                            <div className="text-slate-500">Created</div>
                            <div className="truncate">
                              {(r.created_at || '').slice(0, 10) || '—'}
                            </div>
                          </div>
                        </div>
                        {isKeeper && (
                          <Badge className="bg-emerald-600 text-white text-[10px] uppercase tracking-wide">
                            Keep
                          </Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={merging}
            data-testid="merge-duplicates-cancel"
          >
            Cancel
          </Button>
          {groups.length > 0 && (
            <Button
              onClick={handleMergeAll}
              disabled={merging}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="merge-duplicates-confirm"
            >
              {merging ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Merging…
                </>
              ) : (
                <>Merge {groups.length} group{groups.length === 1 ? '' : 's'}</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
